import { memo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { formatDate, SocketEvents } from '@clinikchat/shared';
import { useAuthStore } from '../../stores/authStore';
import { useCryptoStore } from '../../stores/cryptoStore';
import { getSocket } from '../../lib/socket';
import AttachmentView, { type FileMessageMeta } from '../file/AttachmentView';
import ContextMenu from '../ui/ContextMenu';
import ReactionBar from './ReactionBar';
import type { ReactionGroup } from './ReactionBar';

interface AttachmentData {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  url?: string;
  thumbnailUrl?: string | null;
  /** Channel key epoch this attachment was encrypted under (null = legacy flat key). */
  keyEpoch?: number | null;
  /** True when this attachment has dual-blob DM encryption (senderStorageKey + recipientStorageKey set). */
  isDmDualBlob?: boolean;
}

interface MessageData {
  id: string;
  channelId?: string;
  content: string;
  type?: string;
  encrypted?: boolean;
  createdAt: string;
  editedAt: string | null;
  user?: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    email?: string;
    status?: string;
  };
  attachments?: AttachmentData[];
  /** Parsed FILE-message content JSON (encrypted filename/MIME or DM filename). */
  fileMeta?: FileMessageMeta | null;
  reactions?: ReactionGroup[];
}

interface Props {
  message: MessageData;
  isGrouped: boolean;
  /** userId → displayName for reaction tooltips (from the channel member list). */
  memberNames?: Record<string, string>;
  onEditStart?: (messageId: string, content: string) => void;
  onDelete?: (messageId: string) => void;
  onImageClick?: (url: string, index: number) => void;
  onUserClick?: (
    user: NonNullable<MessageData['user']>,
    position: { x: number; y: number },
  ) => void;
}

// Stable empty array so ReactionBar's "server state changed" effect does not
// fire on every parent render for messages without reactions.
const EMPTY_REACTIONS: ReactionGroup[] = [];

const ICON_CLASS = 'h-4 w-4 md:h-3.5 md:w-3.5';

function ReactIcon() {
  return (
    <svg
      className={ICON_CLASS}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg
      className={ICON_CLASS}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  );
}

function DeleteIcon() {
  return (
    <svg
      className={ICON_CLASS}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

/**
 * Hover / focus action strip. "Add reaction" is available on every message;
 * edit/delete only on the viewer's own. Always visible on touch layouts
 * (no hover), revealed on hover or keyboard focus on md+.
 */
function ActionToolbar({
  canEdit,
  canDelete,
  onReact,
  onEdit,
  onDelete,
  reactButtonRef,
}: {
  canEdit: boolean;
  canDelete: boolean;
  onReact: () => void;
  onEdit: () => void;
  onDelete: () => void;
  /** Lets ReactionBar hand keyboard focus back here when Escape closes the picker. */
  reactButtonRef: RefObject<HTMLButtonElement>;
}) {
  return (
    <div
      role="toolbar"
      aria-label="Message actions"
      className="flex items-center gap-1 opacity-100 transition-opacity duration-150 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
    >
      <button
        ref={reactButtonRef}
        type="button"
        onClick={onReact}
        className="rounded p-2 text-warm-gray-400 hover:bg-warm-gray-200 hover:text-warm-gray-600 md:p-1"
        aria-label="Add reaction"
        title="Add reaction"
      >
        <ReactIcon />
      </button>
      {canEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="rounded p-2 text-warm-gray-400 hover:bg-warm-gray-200 hover:text-warm-gray-600 md:p-1"
          aria-label="Edit message"
          title="Edit"
        >
          <EditIcon />
        </button>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="rounded p-2 text-warm-gray-400 hover:bg-accent-coral-light hover:text-accent-coral md:p-1"
          aria-label="Delete message"
          title="Delete"
        >
          <DeleteIcon />
        </button>
      )}
    </div>
  );
}

function MessageItemImpl({
  message,
  isGrouped,
  memberNames,
  onEditStart,
  onDelete,
  onImageClick,
  onUserClick,
}: Props) {
  const currentUserId = useAuthStore((s) => s.user?.id);
  // Pull channel key from the in-memory store for decrypting encrypted file attachments.
  const channelKey = useCryptoStore((s) =>
    message.channelId ? (s.channelKeys[message.channelId] ?? null) : null,
  );
  // Own X25519 secret key, needed to decrypt DM dual-blob attachments.
  const mySecretKey = useCryptoStore((s) => s.secretKey);
  const isOwn = message.user?.id === currentUserId;
  const displayName = message.user?.displayName || 'Unknown';
  const initial = displayName[0]?.toUpperCase() || '?';
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  // Counter, not boolean: every request to open the picker must reach
  // ReactionBar even if the previous one was already consumed.
  const [pickerSignal, setPickerSignal] = useState(0);

  const canEdit = isOwn && !!onEditStart && message.type !== 'FILE';

  function handleContextMenu(e: React.MouseEvent) {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }

  function handleDelete() {
    // Optimistically remove locally first so mobile users don't need a refresh
    onDelete?.(message.id);
    const socket = getSocket();
    if (socket) {
      socket.emit(SocketEvents.MESSAGE_DELETE, { messageId: message.id });
    }
  }

  function handleAvatarClick(e: React.MouseEvent) {
    if (message.user && onUserClick) {
      onUserClick(message.user, { x: e.clientX, y: e.clientY });
    }
  }

  // Toolbar "Add reaction" button; ReactionBar returns focus here on Escape.

  const reactButtonRef = useRef<HTMLButtonElement>(null);

  const openReactionPicker = () => setPickerSignal((n) => n + 1);
  const startEdit = () => onEditStart?.(message.id, message.content);

  const attachments = (message.attachments || []) as AttachmentData[];
  const reactions = message.reactions ?? EMPTY_REACTIONS;

  const body = (
    <>
      {message.content && (
        <p className="mt-0.5 text-body text-warm-gray-700 dark:text-slate-300 break-words whitespace-pre-wrap">
          {message.content}
        </p>
      )}
      {attachments.length > 0 && (
        <AttachmentView
          attachments={attachments}
          onImageClick={onImageClick || (() => {})}
          encrypted={!!message.encrypted}
          channelKey={channelKey}
          channelId={message.channelId}
          mySecretKey={mySecretKey}
          fileMeta={message.fileMeta}
        />
      )}
      <ReactionBar
        messageId={message.id}
        reactions={reactions}
        memberNames={memberNames}
        openPickerSignal={pickerSignal}
        returnFocusTo={reactButtonRef}
      />
    </>
  );

  const toolbar = (
    <ActionToolbar
      canEdit={canEdit}
      canDelete={isOwn}
      onReact={openReactionPicker}
      onEdit={startEdit}
      onDelete={handleDelete}
      reactButtonRef={reactButtonRef}
    />
  );

  const menu = contextMenu && (
    <ContextMenu
      x={contextMenu.x}
      y={contextMenu.y}
      onClose={() => setContextMenu(null)}
      items={[
        { label: 'Add reaction', onClick: openReactionPicker },
        ...(canEdit ? [{ label: 'Edit message', onClick: startEdit }] : []),
        ...(isOwn ? [{ label: 'Delete message', onClick: handleDelete, danger: true }] : []),
      ]}
    />
  );

  if (isGrouped) {
    return (
      <>
        <div
          className="group flex items-start px-4 py-0.5 hover:bg-warm-gray-50 dark:hover:bg-white/[0.02] md:px-6"
          onContextMenu={handleContextMenu}
        >
          <div className="w-10 flex-shrink-0">
            <span className="hidden text-label text-warm-gray-400 dark:text-warm-gray-600 group-hover:inline">
              {new Date(message.createdAt).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
          <div className="ml-3 min-w-0 flex-1">{body}</div>
          {toolbar}
        </div>
        {menu}
      </>
    );
  }

  return (
    <>
      <div
        className="group flex items-start px-4 py-3 hover:bg-warm-gray-50 dark:hover:bg-white/[0.02] md:px-6"
        onContextMenu={handleContextMenu}
      >
        <div
          className="mr-3 flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-xl bg-primary text-body-sm font-bold text-white overflow-hidden"
          onClick={handleAvatarClick}
        >
          {message.user?.avatarUrl ? (
            <img
              src={message.user.avatarUrl}
              alt={`${message.user?.displayName ?? 'User'} avatar`}
              className="h-10 w-10 rounded-xl object-cover"
            />
          ) : (
            initial
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span
              className="cursor-pointer text-body-sm font-semibold text-primary dark:text-primary hover:underline"
              onClick={handleAvatarClick}
            >
              {displayName}
            </span>
            <span className="text-label text-warm-gray-400 dark:text-warm-gray-600">
              {formatDate(message.createdAt)}
            </span>
            {message.editedAt && (
              <span className="text-label text-warm-gray-400 dark:text-warm-gray-600">
                (edited)
              </span>
            )}
          </div>
          {body}
        </div>
        {toolbar}
      </div>
      {menu}
    </>
  );
}

function sameReactions(a: ReactionGroup[], b: ReactionGroup[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].emoji !== b[i].emoji || a[i].count !== b[i].count) return false;
    // userIds decide the "mine" highlight and the tooltip: a swap of who
    // reacted at an unchanged count must still re-render.
    const au = a[i].userIds;
    const bu = b[i].userIds;
    if (au.length !== bu.length) return false;
    for (let j = 0; j < au.length; j++) {
      if (au[j] !== bu[j]) return false;
    }
  }
  return true;
}

// Memoize so hover/scroll state changes in MessageList don't re-render every
// existing message — a big deal on channels with many messages.
/**
 * Attachments must be compared by VALUE, not length: a refetch re-signs every
 * attachment URL, and a memoised row that kept the old one would leave
 * AttachmentView downloading from an expired link (keyEpoch, filename and
 * isDmDualBlob changes would be ignored the same way).
 */
export function sameAttachments(
  a: readonly object[] | undefined,
  b: readonly object[] | undefined,
): boolean {
  const left = a ?? [];
  const right = b ?? [];
  if (left.length !== right.length) return false;
  return left.every((item, i) => {
    const x = item as Record<string, unknown>;
    const y = right[i] as Record<string, unknown>;
    const keys = new Set([...Object.keys(x), ...Object.keys(y)]);
    for (const key of keys) if (x[key] !== y[key]) return false;
    return true;
  });
}

const MessageItem = memo(MessageItemImpl, (prev, next) => {
  if (prev.isGrouped !== next.isGrouped) return false;
  if (prev.memberNames !== next.memberNames) return false;
  if (prev.message === next.message) return true;
  const a = prev.message;
  const b = next.message;
  if (a.id !== b.id) return false;
  if (a.content !== b.content) return false;
  if (a.editedAt !== b.editedAt) return false;
  // FILE rows keep content '' and decrypt their filename inside AttachmentView
  // from the epoch ring. MessageList re-parses fileMeta (a fresh object) whenever
  // the ring fills, so identity here is what re-renders them.
  if (a.fileMeta !== b.fileMeta) return false;
  if (!sameAttachments(a.attachments, b.attachments)) return false;
  return sameReactions(a.reactions ?? [], b.reactions ?? []);
});

export default MessageItem;
